import { useCallback, useEffect, useRef, useState } from 'react';
import * as Location from 'expo-location';
import { GeoJsonType } from '../../graphql';
import { StorageHandler, useStorageKeys } from '../../storage';
import { Location as LocationModel } from '../domain';
import { useGrippContext } from '../../grippContext';

const isSafari = () => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent;
  return (
    /Safari/i.test(userAgent) &&
    !/Chrome/i.test(userAgent) &&
    !/CriOS/i.test(userAgent) &&
    !/FxiOS/i.test(userAgent)
  );
};

export interface UseWatchLocationOptions {
  disabledInApp?: boolean;
}

export const useWatchLocation = (
  storageHandler: StorageHandler,
  options?: UseWatchLocationOptions
) => {
  const [isWatching, setIsWatching] = useState(false);
  const watchSubscriptionRef = useRef<Location.LocationSubscription | undefined>(undefined);
  const storageKeys = useStorageKeys();
  const disabledInApp = options?.disabledInApp ?? false;

  const stopWatching = useCallback(async () => {
    if (watchSubscriptionRef.current) {
      watchSubscriptionRef.current.remove();
      watchSubscriptionRef.current = undefined;
    }
    setIsWatching(false);
    await storageHandler.deleteItem(storageKeys.location);
  }, [storageHandler, storageKeys.location]);

  const watchLocation = async (requestPermission: boolean) => {
    if (disabledInApp) {
      await stopWatching();
      return;
    }
    if (isWatching) return;

    const permission = requestPermission
      ? await Location.requestForegroundPermissionsAsync()
      : await Location.getForegroundPermissionsAsync();

    if (permission.status === Location.PermissionStatus.GRANTED) {
      setIsWatching(true);
      if (isSafari()) {
        const locationCurrentPosition =
          await Location.getCurrentPositionAsync();
        const location: LocationModel = {
          type: GeoJsonType.Point,
          coordinates: [
            locationCurrentPosition.coords.longitude,
            locationCurrentPosition.coords.latitude,
          ],
        };
        await storageHandler.setItem(
          storageKeys.location,
          JSON.stringify(location)
        );
        return;
      }

      if (watchSubscriptionRef.current) {
        watchSubscriptionRef.current.remove();
      }
      watchSubscriptionRef.current = await Location.watchPositionAsync(
        {
          accuracy: Location.LocationAccuracy.Balanced,
          distanceInterval: 10,
        },
        async (expoLocation: Location.LocationObject) => {
          const location: LocationModel = {
            type: GeoJsonType.Point,
            coordinates: [
              expoLocation.coords.longitude,
              expoLocation.coords.latitude,
            ],
          };

          await storageHandler.setItem(
            storageKeys.location,
            JSON.stringify(location)
          );
        }
      );
    } else {
      setIsWatching(false);
    }
  };

  useEffect(() => {
    if (disabledInApp && (isWatching || watchSubscriptionRef.current)) {
      void stopWatching();
    }
  }, [disabledInApp, isWatching, stopWatching]);

  useEffect(() => {
    return () => {
      if (watchSubscriptionRef.current) {
        watchSubscriptionRef.current.remove();
      }
    };
  }, []);

  return {
    watchLocation: (requestPermission: boolean) =>
      watchLocation(requestPermission),
    isWatching,
  };
};

export interface UseCurrentLocationHook {
  getLocation: () => Promise<LocationModel | undefined>;
}

export const useCurrentLocation = (): UseCurrentLocationHook => {
  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();

  return {
    getLocation: async () => {
      const json = await storageHandler.getItem(storageKeys.location);
      return json ? JSON.parse(json) : undefined;
    },
  };
};
